import { useEffect, useMemo, useState } from 'react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import {LogOut, Users, Package, Link as LinkIcon, Shield} from 'lucide-react'
import {
  ConsultoraAssinatura,
  acessoLiberado,
  coresStatusAssinatura,
  formatarData,
  rotuloStatus,
} from '@/utils/assinatura'
import { copiarTexto } from '@/utils/clipboard'

interface Distribuidora {
  _id: string
  nome: string
  slug: string
  senha: string
  email?: string
  whatsapp?: string
  totalAcessos: number
  ativo: boolean
  createdAt: string
  updatedAt: string
}

type Sessao = { slug: string; nome: string }

function Distribuidora() {
  const [autenticado, setAutenticado] = useState(false)
  const [sessao, setSessao] = useState<Sessao | null>(null)
  const [loginSlug, setLoginSlug] = useState('')
  const [loginSenha, setLoginSenha] = useState('')

  const [distribuidora, setDistribuidora] = useState<Distribuidora | null>(null)
  const [consultoras, setConsultoras] = useState<ConsultoraAssinatura[]>([])
  const [loading, setLoading] = useState(false)

  // Restaurar sessão
  useEffect(() => {
    const raw = localStorage.getItem('distribuidora_sessao')
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Sessao
        setSessao(parsed)
        setAutenticado(true)
      } catch {
        localStorage.removeItem('distribuidora_sessao')
      }
    }
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    const slug = loginSlug.trim().toLowerCase()
    const senha = loginSenha.trim()
    if (!slug || !senha) return toast.error('Preencha usuário e senha')

    try {
      const res = await lumi.entities.distribuidoras.list({ filter: { slug } })
      const dist = (res.list || []).find((d: any) => d.slug === slug)
      if (!dist) return toast.error('Distribuidora não encontrada')
      if (!dist.ativo) return toast.error('Distribuidora inativa. Contate a administração.')
      if (dist.senha !== senha) return toast.error('Senha incorreta')

      const ses: Sessao = { slug: dist.slug, nome: dist.nome }
      setSessao(ses)
      setAutenticado(true)
      localStorage.setItem('distribuidora_sessao', JSON.stringify(ses))
      toast.success(`Bem-vindo, ${dist.nome}!`)
    } catch (e) {
      console.error(e)
      toast.error('Erro ao fazer login')
    }
  }

  const handleLogout = () => {
    setAutenticado(false)
    setSessao(null)
    setDistribuidora(null)
    setConsultoras([])
    localStorage.removeItem('distribuidora_sessao')
  }

  // Carregar dados da distribuidora + consultoras vinculadas
  useEffect(() => {
    if (!autenticado || !sessao) return
    ;(async () => {
      setLoading(true)
      try {
        const [distRes, cRes] = await Promise.all([
          lumi.entities.distribuidoras.list({ filter: { slug: sessao.slug } }),
          lumi.entities.consultoras.list({ filter: { distribuidora: sessao.slug } }),
        ])
        const dist = (distRes.list || []).find((d: any) => d.slug === sessao.slug)
        setDistribuidora(dist || null)
        setConsultoras((cRes.list || []) as ConsultoraAssinatura[])
      } catch (e) {
        console.error(e)
        toast.error('Erro ao carregar dados')
      } finally {
        setLoading(false)
      }
    })()
  }, [autenticado, sessao])

  const utilizados = consultoras.length
  const disponiveis = Math.max(0, (distribuidora?.totalAcessos || 0) - utilizados)

  const linkCadastro = useMemo(
    () => (sessao ? `https://www.descubraseusoleos.com.br/cadastro?d=${sessao.slug}` : ''),
    [sessao]
  )

  const copiarLink = async () => {
    try {
      await copiarTexto(linkCadastro)
      toast.success('Link copiado!')
    } catch {
      toast.error('Erro ao copiar link')
    }
  }

  // ── Tela de login ──
  if (!autenticado) {
    return (
      <div className="min-h-screen bg-bege-fundo flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-lg">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-verde-muito-claro rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-verde-escuro" />
            </div>
            <h1 className="text-3xl font-cormorant font-semibold text-texto-escuro">
              Painel da Distribuidora
            </h1>
            <p className="text-sm text-texto-suave mt-2">
              Acesse seu pacote e acompanhe seus consultores
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-texto-escuro mb-2 text-sm">Usuário (slug)</label>
              <input
                type="text"
                value={loginSlug}
                onChange={e => setLoginSlug(e.target.value)}
                className="w-full px-4 py-3 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                placeholder="seunome"
              />
            </div>
            <div>
              <label className="block text-texto-escuro mb-2 text-sm">Senha</label>
              <input
                type="password"
                value={loginSenha}
                onChange={e => setLoginSenha(e.target.value)}
                className="w-full px-4 py-3 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                placeholder="Sua senha"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-verde-escuro text-white py-3 rounded-full font-medium hover:bg-opacity-90 transition-all"
            >
              Entrar
            </button>
          </form>
        </div>
      </div>
    )
  }

  // ── Painel da distribuidora ──
  return (
    <div className="min-h-screen bg-bege-fundo">
      {/* Header */}
      <div className="bg-verde-escuro text-white px-6 py-5">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <div>
            <h1 className="text-2xl font-cormorant font-semibold">Painel da Distribuidora</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="inline-flex items-center gap-1.5 text-xs bg-white/20 px-3 py-1 rounded-full">
                <Shield className="w-3.5 h-3.5" />
                {distribuidora?.nome || sessao?.nome}
              </span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-white text-verde-escuro px-4 py-2 rounded-full hover:bg-opacity-90 transition-all text-sm font-medium"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {loading ? (
          <div className="bg-white p-8 rounded-xl text-center text-texto-suave">Carregando…</div>
        ) : (
          <>
            {/* Métricas do pacote */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="text-sm text-texto-suave mb-1">Total de acessos</div>
                <div className="text-4xl font-cormorant font-semibold text-texto-escuro">
                  {distribuidora?.totalAcessos || 0}
                </div>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="text-sm text-texto-suave mb-1">Utilizados</div>
                <div className="text-4xl font-cormorant font-semibold text-verde-escuro">
                  {utilizados}
                </div>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="text-sm text-texto-suave mb-1">Disponíveis</div>
                <div className={`text-4xl font-cormorant font-semibold ${disponiveis > 0 ? 'text-verde-claro' : 'text-red-600'}`}>
                  {disponiveis}
                </div>
              </div>
            </div>

            {/* Link de cadastro */}
            <div className="bg-white p-6 rounded-xl shadow-md mb-6">
              <h2 className="text-lg font-cormorant font-semibold text-texto-escuro mb-3 flex items-center gap-2">
                <LinkIcon className="w-5 h-5 text-verde-escuro" />
                Seu link exclusivo de cadastro
              </h2>
              <p className="text-sm text-texto-suave mb-3">
                Compartilhe este link para que novos(as) consultores(as) se cadastrem vinculados ao seu pacote.
              </p>
              <div className="flex flex-col md:flex-row gap-3">
                <code className="flex-1 bg-bege-fundo px-4 py-3 rounded-xl text-sm text-verde-escuro break-all">
                  {linkCadastro}
                </code>
                <button
                  onClick={copiarLink}
                  className="bg-verde-escuro text-white px-5 py-3 rounded-xl hover:bg-opacity-90 transition-all font-medium whitespace-nowrap"
                >
                  Copiar link
                </button>
              </div>
            </div>

            {/* Consultoras vinculadas */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="px-6 py-4 border-b border-bege-medio">
                <h2 className="text-lg font-cormorant font-semibold text-texto-escuro flex items-center gap-2">
                  <Users className="w-5 h-5 text-verde-escuro" />
                  Meus consultores ({consultoras.length})
                </h2>
              </div>
              {consultoras.length === 0 ? (
                <div className="p-8 text-center text-texto-suave">
                  Nenhum consultor cadastrado pelo seu link ainda.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-bege-fundo">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Nome</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">E-mail</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Cadastro</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Expira em</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-bege-medio">
                      {consultoras.map(c => {
                        const cores = coresStatusAssinatura(c.statusAssinatura)
                        const ativo = acessoLiberado(c)
                        return (
                          <tr key={c._id} className="hover:bg-bege-fundo">
                            <td className="px-4 py-3 text-sm text-texto-escuro font-medium">{c.nome}</td>
                            <td className="px-4 py-3 text-sm text-texto-suave">{c.email || '—'}</td>
                            <td className="px-4 py-3 text-sm text-texto-suave">{formatarData(c.createdAt)}</td>
                            <td className="px-4 py-3 text-sm text-texto-suave">
                              {c.dataExpiracao ? formatarData(c.dataExpiracao) : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className="px-3 py-1 rounded-full text-xs font-medium"
                                style={{ backgroundColor: cores.bg, color: cores.text }}
                              >
                                {rotuloStatus(c.statusAssinatura)}
                                {!ativo && c.statusAssinatura !== 'expirado' && c.statusAssinatura !== 'inativo'
                                  ? ' (expirado)'
                                  : ''}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <p className="text-xs text-texto-suave text-center mt-6">
              Você visualiza apenas os dados dos seus consultores. Os leads de diagnóstico de cada consultor(a) são
              privados e não aparecem aqui.
            </p>
          </>
        )}
      </div>
    </div>
  )
}

export default Distribuidora
